import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Table, Tag, Modal, Form, Input, Select, Switch } from "antd";
import { FiGrid, FiPlus, FiEdit, FiTrash2, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiLink } from "react-icons/fi";

export default function IntegrationsPage() {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editingIntegration, setEditingIntegration] = useState(null);
	const [form] = Form.useForm();

	// Integrations Data
	const integrationsData = [
		{
			id: "INT-001",
			name: "Splunk Enterprise",
			category: "SIEM",
			status: "Connected",
			description: "Security Information and Event Management system for log aggregation",
			endpoint: "splunk.sec1.local:8089",
			lastSync: "2025-01-02 14:30:00",
			dataFlow: "Bidirectional",
			enabled: true,
		},
		{
			id: "INT-002",
			name: "Nessus Scanner",
			category: "Vulnerability Management",
			status: "Connected",
			description: "Vulnerability scanning and assessment tool",
			endpoint: "nessus.sec1.local:8834",
			lastSync: "2025-01-02 12:15:00",
			dataFlow: "Unidirectional",
			enabled: true,
		},
		{
			id: "INT-003",
			name: "Zeek Network Monitor",
			category: "Network Monitoring",
			status: "Connected",
			description: "Network traffic analysis and intrusion detection",
			endpoint: "zeek.sec1.local:9201",
			lastSync: "2025-01-02 14:45:00",
			dataFlow: "Unidirectional",
			enabled: true,
		},
		{
			id: "INT-004",
			name: "ServiceNow ITSM",
			category: "Ticketing",
			status: "Connected",
			description: "IT Service Management and incident tracking",
			endpoint: "sec1.service-now.com",
			lastSync: "2025-01-02 11:20:00",
			dataFlow: "Bidirectional",
			enabled: true,
		},
		{
			id: "INT-005",
			name: "Microsoft Active Directory",
			category: "Authentication",
			status: "Connected",
			description: "User authentication and access control system",
			endpoint: "ad.sec1.local:389",
			lastSync: "2025-01-02 15:00:00",
			dataFlow: "Unidirectional",
			enabled: true,
		},
		{
			id: "INT-006",
			name: "Slack Notifications",
			category: "Communication",
			status: "Connected",
			description: "Real-time alert notifications to Slack channels",
			endpoint: "hooks.slack.com/api",
			lastSync: "2025-01-02 13:30:00",
			dataFlow: "Unidirectional",
			enabled: true,
		},
		{
			id: "INT-007",
			name: "Qualys VMDR",
			category: "Vulnerability Management",
			status: "Disconnected",
			description: "Vulnerability, Media and Detection Response platform",
			endpoint: "qualys.sec1.api",
			lastSync: "2024-12-28 10:00:00",
			dataFlow: "Unidirectional",
			enabled: false,
		},
		{
			id: "INT-008",
			name: "ELK Stack",
			category: "Log Management",
			status: "Connected",
			description: "Elasticsearch, Logstash, Kibana for log management",
			endpoint: "elasticsearch.sec1.local:9200",
			lastSync: "2025-01-02 14:55:00",
			dataFlow: "Bidirectional",
			enabled: true,
		},
		{
			id: "INT-009",
			name: "AWS CloudTrail",
			category: "Cloud Integration",
			status: "Connected",
			description: "AWS API activity logging and monitoring",
			endpoint: "cloudtrail.amazonaws.com",
			lastSync: "2025-01-02 15:15:00",
			dataFlow: "Unidirectional",
			enabled: true,
		},
		{
			id: "INT-010",
			name: "MQTT Broker",
			category: "IoT/OT",
			status: "Connected",
			description: "Message Queuing Telemetry Transport for OT device communication",
			endpoint: "mqtt.sec1.local:1883",
			lastSync: "2025-01-02 14:20:00",
			dataFlow: "Bidirectional",
			enabled: true,
		},
	];

	// Summary Statistics
	const integrationStats = {
		totalIntegrations: integrationsData.length,
		connected: integrationsData.filter(i => i.status === "Connected").length,
		disconnected: integrationsData.filter(i => i.status === "Disconnected").length,
		categories: new Set(integrationsData.map(i => i.category)).size,
	};

	// Helper functions
	const getStatusColor = (status) => {
		return status === "Connected" ? "#10b981" : "#ef4444";
	};

	const getCategoryColor = (category) => {
		const colors = {
			SIEM: "#3b82f6",
			"Vulnerability Management": "#ef4444",
			"Network Monitoring": "#f59e0b",
			Ticketing: "#8b5cf6",
			Authentication: "#10b981",
			Communication: "#06b6d4",
			"Log Management": "#ec4899",
			"Cloud Integration": "#f97316",
			"IoT/OT": "#14b8a6",
		};
		return colors[category] || "#6b7280";
	};

	const getDataFlowBg = (flow) => {
		return flow === "Bidirectional" ? "rgba(59, 130, 246, 0.1)" : "rgba(107, 114, 128, 0.1)";
	};

	const handleAddIntegration = () => {
		setEditingIntegration(null);
		form.resetFields();
		setIsModalVisible(true);
	};

	const handleEditIntegration = (integration) => {
		setEditingIntegration(integration);
		form.setFieldsValue(integration);
		setIsModalVisible(true);
	};

	const handleModalOk = () => {
		form.validateFields().then(() => {
			setIsModalVisible(false);
		});
	};

	const handleModalCancel = () => {
		setIsModalVisible(false);
	};

	// Table columns
	const integrationColumns = [
		{
			title: "Integration ID",
			dataIndex: "id",
			key: "id",
			render: (text) => <span className="fw-bold text-gray-900">{text}</span>,
			width: 100,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text) => <span className="fw-semibold text-gray-900">{text}</span>,
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			render: (category) => (
				<Tag style={{ borderRadius: "4px", background: `rgba(${hexToRgb(getCategoryColor(category))}, 0.1)`, color: getCategoryColor(category), fontWeight: "600" }}>
					{category}
				</Tag>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<div className="d-flex align-items-center gap-2">
					{status === "Connected" ? (
						<FiCheckCircle size={16} color="#10b981" />
					) : (
						<FiAlertCircle size={16} color="#ef4444" />
					)}
					<Tag color={getStatusColor(status)} style={{ borderRadius: "4px", fontWeight: "600" }}>
						{status}
					</Tag>
				</div>
			),
			width: 120,
		},
		{
			title: "Data Flow",
			dataIndex: "dataFlow",
			key: "dataFlow",
			render: (flow) => (
				<Tag style={{ borderRadius: "4px", background: getDataFlowBg(flow), color: "#6b7280", fontWeight: "600" }}>
					{flow}
				</Tag>
			),
			width: 130,
		},
		{
			title: "Endpoint",
			dataIndex: "endpoint",
			key: "endpoint",
			render: (endpoint) => <span className="text-gray-700 fs-6">{endpoint}</span>,
		},
		{
			title: "Last Sync",
			dataIndex: "lastSync",
			key: "lastSync",
			width: 160,
		},
		{
			title: "Enabled",
			dataIndex: "enabled",
			key: "enabled",
			render: (enabled) => (
				<Switch checked={enabled} disabled />
			),
			width: 80,
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<div className="d-flex gap-2">
					<button
						type="button"
						className="btn btn-sm btn-light-primary fw-bold"
						onClick={() => handleEditIntegration(record)}
						title="Edit integration"
					>
						<FiEdit size={14} />
					</button>
					<button type="button" className="btn btn-sm btn-light-danger fw-bold" title="Delete integration">
						<FiTrash2 size={14} />
					</button>
				</div>
			),
			width: 100,
		},
	];

	// Helper function to convert hex to RGB
	const hexToRgb = (hex) => {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "0, 0, 0";
	};

	return (
		<>
			<Helmet>
				<title>Integrations | Sec1 Software Pvt. Ltd.</title>
			</Helmet>

			<div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0">
				<div id="kt_app_toolbar_container" className="app-container container-fluid d-flex align-items-stretch">
					<div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
						<div className="page-title d-flex flex-column gap-1 me-3 mb-2">
							<h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
								Third-Party Integrations
							</h1>
						</div>
					</div>
				</div>
			</div>

			<div id="kt_app_content" className="app-content flex-column-fluid">
				<div id="kt_app_content_container" className="app-container container-fluid">
					{/* Statistics Cards */}
					<div className="mb-3">
						<div className="row g-2">
							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiGrid className="fs-2hx text-primary" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{integrationStats.totalIntegrations}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Total Integrations</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiCheckCircle className="fs-2hx text-success" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{integrationStats.connected}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Connected</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiAlertCircle className="fs-2hx text-danger" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{integrationStats.disconnected}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Disconnected</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiLink className="fs-2hx text-info" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{integrationStats.categories}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Categories</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Integrations Table Section */}
					<div className="card">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Connected Integrations</span>
								<span className="text-muted mt-1 fw-semibold fs-7">Third-party tools and services connected to the system</span>
							</h3>
							<div className="card-toolbar">
								<button
									type="button"
									className="btn btn-sm btn-primary fw-bold d-flex align-items-center gap-2"
									onClick={handleAddIntegration}
								>
									<FiPlus size={16} />
									Add Integration
								</button>
							</div>
						</div>
						<div className="card-body">
							<Table
								dataSource={integrationsData}
								columns={integrationColumns}
								pagination={{ pageSize: 10, total: integrationsData.length }}
								scroll={{ x: 1500 }}
								className="antd-override"
								rowKey={(record) => record.id}
							/>
						</div>
					</div>

					{/* Integration Categories Overview */}
					<div className="card mt-3">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Integration Categories</span>
								<span className="text-muted mt-1 fw-semibold fs-7">Overview of integration types and their purposes</span>
							</h3>
						</div>
						<div className="card-body">
							<div className="row g-3">
								{/* SIEM Category */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #3b82f6", boxShadow: "0 2px 8px rgba(59, 130, 246, 0.08)" }}>
										<h6 className="fw-bold text-gray-900 mb-2">Security Information & Event Management</h6>
										<p className="text-gray-600 fs-6 mb-2">SIEM systems for centralized log management and threat detection</p>
										<div className="d-flex gap-2 flex-wrap">
											<Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>Splunk</Tag>
											<Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>ELK Stack</Tag>
										</div>
									</div>
								</div>

								{/* Vulnerability Management Category */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #ef4444", boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08)" }}>
										<h6 className="fw-bold text-gray-900 mb-2">Vulnerability Management</h6>
										<p className="text-gray-600 fs-6 mb-2">Tools for vulnerability scanning, assessment, and management</p>
										<div className="d-flex gap-2 flex-wrap">
											<Tag style={{ borderRadius: "4px", background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" }}>Nessus</Tag>
											<Tag style={{ borderRadius: "4px", background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" }}>Qualys</Tag>
										</div>
									</div>
								</div>

								{/* Network Monitoring Category */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #f59e0b", boxShadow: "0 2px 8px rgba(245, 158, 11, 0.08)" }}>
										<h6 className="fw-bold text-gray-900 mb-2">Network Monitoring</h6>
										<p className="text-gray-600 fs-6 mb-2">Network traffic analysis and intrusion detection systems</p>
										<div className="d-flex gap-2 flex-wrap">
											<Tag style={{ borderRadius: "4px", background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>Zeek</Tag>
											<Tag style={{ borderRadius: "4px", background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>Wireshark</Tag>
										</div>
									</div>
								</div>

								{/* OT/IoT Protocols Category */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(20, 184, 166, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #14b8a6", boxShadow: "0 2px 8px rgba(20, 184, 166, 0.08)" }}>
										<h6 className="fw-bold text-gray-900 mb-2">OT/IoT Communication</h6>
										<p className="text-gray-600 fs-6 mb-2">Industrial protocols and messaging systems for device communication</p>
										<div className="d-flex gap-2 flex-wrap">
											<Tag style={{ borderRadius: "4px", background: "rgba(20, 184, 166, 0.1)", color: "#14b8a6" }}>MQTT</Tag>
											<Tag style={{ borderRadius: "4px", background: "rgba(20, 184, 166, 0.1)", color: "#14b8a6" }}>Modbus</Tag>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Add/Edit Integration Modal */}
			<Modal
				title={editingIntegration ? "Edit Integration" : "Add New Integration"}
				visible={isModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				width={600}
			>
				<Form form={form} layout="vertical">
					<Form.Item label="Integration Name" name="name" rules={[{ required: true, message: "Please enter integration name" }]}>
						<Input placeholder="e.g., Splunk Enterprise" />
					</Form.Item>
					<Form.Item label="Category" name="category" rules={[{ required: true, message: "Please select category" }]}>
						<Select placeholder="Select category">
							<Select.Option value="SIEM">SIEM</Select.Option>
							<Select.Option value="Vulnerability Management">Vulnerability Management</Select.Option>
							<Select.Option value="Network Monitoring">Network Monitoring</Select.Option>
							<Select.Option value="Ticketing">Ticketing</Select.Option>
							<Select.Option value="Authentication">Authentication</Select.Option>
							<Select.Option value="Communication">Communication</Select.Option>
							<Select.Option value="Log Management">Log Management</Select.Option>
							<Select.Option value="Cloud Integration">Cloud Integration</Select.Option>
							<Select.Option value="IoT/OT">IoT/OT</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Endpoint" name="endpoint" rules={[{ required: true, message: "Please enter endpoint" }]}>
						<Input placeholder="e.g., splunk.sec1.local:8089" />
					</Form.Item>
					<Form.Item label="Data Flow" name="dataFlow" rules={[{ required: true, message: "Please select data flow type" }]}>
						<Select placeholder="Select data flow type">
							<Select.Option value="Unidirectional">Unidirectional</Select.Option>
							<Select.Option value="Bidirectional">Bidirectional</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Enabled" name="enabled" valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
}
